import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { BookingSmartPromotionService } from '@products/services/booking-smart-promotion.service';
import { PackageProductBookingActions } from '@store/products/booking/product/package/package-booking.actions';
import { ProductBookingActions } from '@store/products/booking/product/product-booking.actions';
import { VoucherProductBookingActions } from '@store/products/booking/product/voucher/voucher-booking.actions';
import { BookingSmartPromotionActions } from '@store/products/booking/smart-promotion/booking-smart-promotion.actions';
import { selectAllDiscountPromoCodes } from '@store/products/booking/smart-promotion/booking-smart-promotion.selectors';
import { SmartPromotionActions } from '@store/products/product.actions';
import { isEmpty } from 'lodash';
import { catchError, EMPTY, map, of, switchMap } from 'rxjs';

export const redeemPromoCode$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(SmartPromotionActions.redeemPromoCode),
      map(({ redeemPromoCode }) => BookingSmartPromotionActions.setPromoCodeDiscount(redeemPromoCode)),
      catchError(() => of(BookingSmartPromotionActions.setPromoCodeDiscount(null)))
    ),
  { functional: true }
);

export const redeemPromoCodeOnConfirmationPageInit$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(SmartPromotionActions.redeemPromoCodeOnConfirmationStepInit),
      map(() => BookingSmartPromotionActions.setPromoCodeDiscountOnConfirmationStepInit()),
      catchError(() => of(BookingSmartPromotionActions.setPromoCodeDiscount(null)))
    ),
  { functional: true }
);

export const removeRedeemedPromoCode$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(SmartPromotionActions.removeRedeemedPromoCode),
      concatLatestFrom(() => store.select(selectAllDiscountPromoCodes)),
      switchMap(([{ removePromoCode }, promoCodes]) => {
        if (isEmpty(removePromoCode)) {
          return EMPTY;
        }

        const activePromoCodes = promoCodes.filter((activePromoCode) => activePromoCode.promoCode !== removePromoCode);

        const dispatchAction = isEmpty(activePromoCodes)
          ? BookingSmartPromotionActions.removeAndReleasePromoCodeDiscount()
          : BookingSmartPromotionActions.setPromoCodeDiscount(removePromoCode);

        return of(dispatchAction);
      }),
      catchError(() => of(BookingSmartPromotionActions.setPromoCodeDiscount(null)))
    ),
  { functional: true }
);

export const removeUnredeemedPromoCode$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(SmartPromotionActions.removeUnredeemedPromoCode),
      map(({ removePromoCode }) => BookingSmartPromotionActions.removeUnredeemedPromoCode(removePromoCode)),
      catchError(() => of(BookingSmartPromotionActions.removeUnredeemedPromoCode(null)))
    ),
  { functional: true }
);

export const setPromoCodeDiscount$ = createEffect(
  (actions$ = inject(Actions), bookingSmartPromotionService = inject(BookingSmartPromotionService)) =>
    actions$.pipe(
      ofType(BookingSmartPromotionActions.setPromoCodeDiscount),
      switchMap(({ redeemPromoCode }) => {
        if (isEmpty(redeemPromoCode)) {
          return EMPTY;
        }

        return bookingSmartPromotionService.setBookingPromoCodeDiscount$(redeemPromoCode);
      })
    ),
  { functional: true }
);

export const setPromoCodeDiscountOnConfirmationStepInit$ = createEffect(
  (actions$ = inject(Actions), bookingSmartPromotionService = inject(BookingSmartPromotionService)) =>
    actions$.pipe(
      ofType(BookingSmartPromotionActions.setPromoCodeDiscountOnConfirmationStepInit),
      switchMap(() => bookingSmartPromotionService.setBookingPromoCodeDiscount$())
    ),
  { functional: true }
);

export const removeBookedProductsPromoCodeDiscountOnChangedProductBookings$ = createEffect(
  (actions$ = inject(Actions), bookingSmartPromotionService = inject(BookingSmartPromotionService)) =>
    actions$.pipe(
      ofType(
        ProductBookingActions.setProductInBookingList,
        ProductBookingActions.removeBookedProductsAndSetPreferredProductInBookingList,
        ProductBookingActions.removeProductFromBookingList,
        VoucherProductBookingActions.setVoucherProductInBookingList,
        VoucherProductBookingActions.removeVoucherProductFromBookingList,
        VoucherProductBookingActions.removeReleasedVoucherProductFromBookingList,
        PackageProductBookingActions.setPackageProductInBookingList,
        PackageProductBookingActions.removePackageFromBookingList
      ),
      switchMap(() =>
        bookingSmartPromotionService
          .releasePromoCodeDiscountReservation$()
          .pipe(map(() => BookingSmartPromotionActions.removeBookedProductsPromoCodeDiscountOnChangedProductBookings()))
      ),
      catchError(() => EMPTY)
    ),
  { functional: true }
);

export const removeAndReleasePromoCodeDiscount$ = createEffect(
  (actions$ = inject(Actions), bookingSmartPromotionService = inject(BookingSmartPromotionService)) =>
    actions$.pipe(
      ofType(
        BookingSmartPromotionActions.removeAndReleasePromoCodeDiscount,
        VoucherProductBookingActions.setVoucherForAnonymousProductInBookingList
      ),
      switchMap(() =>
        bookingSmartPromotionService
          .releasePromoCodeDiscountReservation$()
          .pipe(map(() => BookingSmartPromotionActions.removeBookedProductsPromoCodeDiscount()))
      ),
      catchError(() => EMPTY)
    ),
  { functional: true }
);
